import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { TEAM } from "../../data/site";

const slug = (m) => m.name.split(" ")[0].toLowerCase();

const TeamCard = ({ m, i }) => (
    <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
        className="w-[78%] shrink-0 snap-start sm:w-auto"
    >
        <Link
            to={m.bio ? `/about#team-${slug(m)}` : "/about"}
            data-testid={`home-team-card-${slug(m)}`}
            className="group relative block overflow-hidden rounded-3xl border border-amber-500/20 bg-[#1F1510] transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-amber-400/60 hover:shadow-[0_18px_60px_-20px_rgba(251,191,36,0.35)]"
        >
            <div className="relative aspect-[4/5] overflow-hidden">
                <img
                    src={`/images/${m.img}`}
                    alt={`${m.name}, ${m.role} at NeuroPlay Counseling`}
                    loading="lazy"
                    className="h-full w-full object-cover object-[center_25%] transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#140d09] via-[#140d09]/35 to-transparent" />
                <ArrowUpRight className="absolute right-4 top-4 h-5 w-5 text-amber-100/0 transition-colors duration-300 group-hover:text-amber-100/90" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                    <p className="font-serif text-xl text-amber-50">{m.name}</p>
                    <p className="text-xs text-amber-300/85">
                        {m.role}
                        {m.creds && m.role !== "Therapy Dog" ? ` · ${m.creds}` : ""}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {(m.specialties || []).slice(0, 3).map((s) => (
                            <span
                                key={s}
                                className="rounded-full border border-amber-200/20 bg-black/35 px-2.5 py-1 text-[11px] leading-none text-amber-100/85 backdrop-blur-md"
                            >
                                {s}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </Link>
    </motion.div>
);

export default function MeetTheTeam() {
    return (
        <section
            id="team-section"
            data-testid="home-team-section"
            className="relative mx-auto w-full max-w-6xl scroll-mt-20 px-6 py-24 sm:py-28"
        >
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
            >
                <div className="flex items-baseline gap-4">
                    <span className="font-serif text-sm tracking-[0.35em] text-amber-500">—</span>
                    <span className="h-px flex-1 bg-amber-500/20" aria-hidden="true" />
                    <span className="text-xs uppercase tracking-[0.3em] text-amber-200/50">Meet the Team</span>
                </div>
                <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <h2 className="max-w-xl font-serif text-2xl tracking-tight text-amber-50 sm:text-3xl lg:text-4xl">
                        The faces behind the candlelight
                    </h2>
                    <Link
                        to="/about"
                        data-testid="home-team-all-link"
                        className="inline-flex items-center gap-1.5 text-sm text-amber-300 transition-colors hover:text-amber-100"
                    >
                        Read every story <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </div>
            </motion.div>
            <div className="-mx-6 mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto px-6 pb-4 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-3">
                {TEAM.map((m, i) => (
                    <TeamCard key={m.name} m={m} i={i} />
                ))}
            </div>
        </section>
    );
}
